// Las 3 primeras piezas del onboarding de marca (ADR-2026-09-28-onboarding-v2-etapa-1).
//
// Primero se le pregunta a la persona QUÉ quiere generar —4 opciones y "Otro" con su
// texto— y las 3 piezas son 3 VARIANTES de eso, con encuadres distintos para que no
// salgan iguales (ADR-2026-09-29-onboarding-v2-piezas-a-pedido). Cada una se arma con lo
// que el análisis encontró —paleta con roles, estilo tipográfico, público— y trae UN
// porqué corto. Se lanzan como un lote del agente (`/v1/agent/lote`): 1 crédito por
// imagen entregada, igual que cualquier generación.

import type { ColorDeMarca, TipografiasDeMarca } from './escaneo';
import { primerasOraciones, seccionesDelAnalisis } from './tablero';

export type UsoDelLogo = 'integrado' | 'agua' | 'sin';

export interface MarcaParaPiezas {
  nombre: string;
  rubro: string;
  colores: ColorDeMarca[];
  tipografias: TipografiasDeMarca;
  analisis: string;
  logoUrl: string | null;
}

/** Las 3 iteraciones. Son ids de ítem del lote: `[A-Za-z0-9_-]`. */
export type PiezaId = 'v1' | 'v2' | 'v3';

export interface PlanDePieza {
  id: PiezaId;
  etiqueta: string;
  prompt: string;
  /** Por qué se ve así: UN ítem corto, debajo de la pieza y en el revelado. */
  porques: string[];
}

export const FORMATO_DE_LAS_PIEZAS = '4:5';

/** Qué quiere generar la persona. */
export type TipoDePieza = 'producto' | 'anuncio' | 'en_uso' | 'promo' | 'otro';

export interface ObjetivoDePiezas {
  tipo: TipoDePieza;
  /** Lo que escribió en "Otro". */
  otro?: string;
}

export const OPCIONES_DE_PIEZA: ReadonlyArray<{ id: Exclude<TipoDePieza, 'otro'>; titulo: string; detalle: string }> = [
  { id: 'producto', titulo: 'Fotos de producto', detalle: 'Tu producto solo, con fondo limpio, para tu tienda o catálogo.' },
  { id: 'anuncio', titulo: 'Anuncios para redes', detalle: 'Con un titular corto y un botón, listos para pautar.' },
  { id: 'en_uso', titulo: 'Tu producto en uso', detalle: 'Escenas reales, para tus posteos.' },
  { id: 'promo', titulo: 'Una promoción', detalle: 'Para una oferta, un descuento o una fecha especial.' },
];

/** El nombre de lo que eligió, para los títulos ("3 opciones de …"). */
export function nombreDelObjetivo(o: ObjetivoDePiezas): string {
  if (o.tipo === 'otro') return o.otro?.trim() || 'lo que pediste';
  return OPCIONES_DE_PIEZA.find((x) => x.id === o.tipo)?.titulo ?? '';
}

/**
 * Lo que eligió en "¿Qué te gustaría crear primero?" durante la espera, como punto de partida:
 * la pregunta de las piezas arranca marcada con eso y la persona la cambia si quiere.
 */
export function objetivoSugerido(primero: string | undefined, otroPrimero?: string): ObjetivoDePiezas {
  if (primero === 'foto_producto') return { tipo: 'producto' };
  if (primero === 'fotos_redes') return { tipo: 'en_uso' };
  if (primero === 'otro' && otroPrimero?.trim()) return { tipo: 'otro', otro: otroPrimero.trim() };
  return { tipo: 'anuncio' };
}

function colorPorRol(colores: ColorDeMarca[], rol: string): ColorDeMarca | null {
  return colores.find((c) => c.role === rol) ?? null;
}

const nombreDeColor = (c: ColorDeMarca) => (c.name?.trim() ? `${c.name.trim()} (${c.hex})` : c.hex);

/** "Negro (#000000), Rojo (#DE2A2A)…" — la paleta como la lee el modelo. */
export function describirPaleta(colores: ColorDeMarca[]): string {
  return colores.map(nombreDeColor).join(', ');
}

function estiloTipografico(t: TipografiasDeMarca): string {
  const estilo = t.style?.trim();
  const fuente = t.suggested?.find((f) => f.trim());
  if (estilo && fuente) return `${estilo} (similar a ${fuente})`;
  return estilo || (fuente ? `similar a ${fuente}` : 'sans serif limpia y moderna');
}

function instruccionDeLogo(uso: UsoDelLogo, conLogo: boolean): string {
  if (uso === 'integrado' && conLogo) {
    return 'Incluí el logo de la referencia como parte del diseño, arriba a la izquierda, chico y nítido. Copialo exacto: no lo redibujes ni cambies sus colores.';
  }
  return 'No agregues logos, marcas de agua ni nombres de marca dentro de la imagen.';
}

interface Variante {
  etiqueta: string;
  /** Lo que cambia entre iteraciones: encuadre, fondo, escena. */
  instruccion: string;
  /**
   * Para qué sirve esta pieza, como lo dice Cumbre debajo del título en el revelado. Habla
   * de OTRA cosa que la etiqueta (qué logra, dónde usarla): repetirla no suma nada.
   */
  porque: string;
}

/**
 * Los tres planes: 3 variantes de lo que eligió la persona. `producto` es el nombre que
 * puso (o "el producto"). `uso` decide si el logo va en la imagen: con "marca de agua" se
 * aplica al descargar, con la configuración de la marca, así que acá tampoco va.
 */
export function planDePiezas(
  marca: MarcaParaPiezas,
  producto: string,
  uso: UsoDelLogo,
  objetivo: ObjetivoDePiezas,
): PlanDePieza[] {
  const nombreProducto = producto.trim() || 'el producto';
  const principal = colorPorRol(marca.colores, 'primary') ?? marca.colores[0] ?? null;
  const acento = colorPorRol(marca.colores, 'accent') ?? colorPorRol(marca.colores, 'secondary');
  const neutro = colorPorRol(marca.colores, 'neutral');
  const paleta = describirPaleta(marca.colores);
  const letra = estiloTipografico(marca.tipografias);
  const secciones = seccionesDelAnalisis(marca.analisis);
  const publico = secciones[2] ? primerasOraciones(secciones[2], 1) : '';
  const estetica = secciones[3] ? primerasOraciones(secciones[3], 2) : '';
  const quien = marca.rubro ? `${marca.nombre}, marca de ${marca.rubro.toLowerCase()}` : marca.nombre;
  const deColor = (c: ColorDeMarca | null, sin: string) => (c ? ` en ${nombreDeColor(c)}` : sin);

  let pedido: string;
  let variantes: [Variante, Variante, Variante];
  switch (objetivo.tipo) {
    case 'producto':
      pedido = `Foto de producto para la tienda online de ${quien}. Sin texto sobreimpreso.`;
      variantes = [
        {
          etiqueta: 'Frente, fondo claro',
          instruccion: `Producto de frente, centrado, sobre un fondo liso${deColor(neutro, ' claro')}, con luz de estudio suave y sombra natural.`,
          porque: 'La primera de tu tienda: nada compite con el producto.',
        },
        {
          etiqueta: 'En ángulo, tu color',
          instruccion: `Producto en ángulo de tres cuartos sobre un fondo${deColor(principal, ' de color liso')}, con luz lateral que marque el volumen.`,
          porque: 'Muestra el volumen: ideal como segunda foto.',
        },
        {
          etiqueta: 'Detalle',
          instruccion: 'Primer plano de un detalle real del producto (textura, terminación o material) que se vea en la referencia. No inventes detalles.',
          porque: 'Convence a quien duda antes de comprar.',
        },
      ];
      break;
    case 'promo':
      pedido = `Pieza de promoción para ${quien}. Texto dentro de la imagen, en español y en tipografía ${letra}.`;
      variantes = [
        {
          etiqueta: 'Sello de oferta',
          instruccion: `Producto grande con un sello circular que diga "OFERTA"${deColor(acento, '')} y un botón "Aprovechá" abajo.`,
          porque: 'Se entiende de un vistazo que hay una oportunidad.',
        },
        {
          etiqueta: 'Descuento destacado',
          instruccion: `Un "% OFF" grande como protagonista junto al producto, sobre un fondo${deColor(principal, ' de color liso')}, y un botón "Comprá ahora".`,
          porque: 'El número es lo primero que se lee: ideal para historias.',
        },
        {
          etiqueta: 'Por tiempo limitado',
          instruccion: 'Composición con sensación de urgencia: producto al costado y el texto "Sólo por tiempo limitado" con un botón "Aprovechá".',
          porque: 'Empuja a decidir hoy, sin gritar.',
        },
      ];
      break;
    case 'en_uso':
      pedido = `Foto para redes sociales de ${quien}: el producto en uso, en un lugar real y creíble${publico ? ` para este público: ${publico}` : ''}. Sin texto sobreimpreso. Encuadre vertical, sensación auténtica.`;
      variantes = [
        {
          etiqueta: 'Adentro, luz natural',
          instruccion: 'Escena de interior con luz natural de ventana, cálida y cercana.',
          porque: 'Cálida, como el posteo de alguien que lo usa.',
        },
        {
          etiqueta: 'Afuera',
          instruccion: 'Escena al aire libre o en la ciudad, con luz de día.',
          porque: 'Lo pone en la vida diaria de tu público.',
        },
        {
          etiqueta: 'De cerca, en uso',
          instruccion: 'Plano cercano del producto mientras se usa (en manos o en contexto), con fondo desenfocado.',
          porque: 'Las manos y el gesto cuentan cómo se siente usarlo.',
        },
      ];
      break;
    case 'otro':
      pedido = `Pieza para ${quien}. Lo que pidió la persona: "${objetivo.otro?.trim() || 'una pieza para redes'}". Seguilo al pie de la letra; si lleva texto, que sea en español y en tipografía ${letra}.`;
      variantes = [
        {
          etiqueta: 'Opción centrada',
          instruccion: 'Composición centrada y simple, con el producto como protagonista.',
          porque: 'La más directa: el producto es lo único que se mira.',
        },
        {
          etiqueta: 'Opción con aire',
          instruccion: 'Composición asimétrica con mucho espacio libre alrededor.',
          porque: 'Deja espacio para sumarle texto o tu logo después.',
        },
        {
          etiqueta: 'Opción de cerca',
          instruccion: 'Plano más cercano, que llene el cuadro.',
          porque: 'Muestra los detalles que en una foto lejana se pierden.',
        },
      ];
      break;
    case 'anuncio':
    default:
      pedido = `Anuncio para redes de ${quien}. Texto dentro de la imagen, en español y en tipografía ${letra}: un titular corto con el nombre del producto y un botón "Comprá ahora"${deColor(principal, '')}.`;
      variantes = [
        {
          etiqueta: 'Titular arriba',
          instruccion: `Titular arriba, producto grande al centro sobre un fondo${deColor(neutro, ' limpio')}, botón abajo.`,
          porque: 'Se lee de un tirón y termina en el botón de compra.',
        },
        {
          etiqueta: 'Producto al costado',
          instruccion: `Producto a un lado y el texto al otro, sobre un fondo${deColor(principal, ' de color liso')}.`,
          porque: 'Funciona en el feed: el mensaje y lo que vendes, a la vez.',
        },
        {
          etiqueta: 'Minimalista',
          instruccion: 'Composición minimalista con mucho espacio libre, titular chico y producto como única figura.',
          porque: 'Elegante: se destaca entre anuncios cargados.',
        },
      ];
  }

  const comunes = [
    `El producto es el de la referencia ("${nombreProducto}"): respetá exactamente su forma, color, materiales y detalles.`,
    paleta ? `Usá sólo colores de la marca: ${paleta}.` : '',
    estetica ? `Estética de la marca: ${estetica}` : '',
    instruccionDeLogo(uso, Boolean(marca.logoUrl)),
  ];

  return variantes.map((v, i) => ({
    id: (['v1', 'v2', 'v3'] as const)[i]!,
    etiqueta: v.etiqueta,
    prompt: [pedido, comunes[0], v.instruccion, ...comunes.slice(1)].filter(Boolean).join('\n'),
    porques: [v.porque],
  }));
}

export interface ItemDelLote {
  id: PiezaId;
  etiqueta: string;
  prompt: string;
  cantidad: 1;
  reference_images: Array<{ tipo: 'producto' | 'logo'; product_id?: string; url?: string }>;
}

/**
 * Los ítems de `/v1/agent/lote`. El producto va por `product_id` (la VARIANTE: el
 * worker baja sus fotos). El logo va como referencia sólo si se integra al diseño; tiene
 * que estar en nuestro Storage, que es donde el escaneo lo dejó.
 */
export function itemsDelLote(
  planes: PlanDePieza[],
  p: { varianteId: string; logoUrl: string | null; uso: UsoDelLogo },
): ItemDelLote[] {
  return planes.map((plan) => ({
    id: plan.id,
    etiqueta: plan.etiqueta,
    prompt: plan.prompt,
    cantidad: 1,
    reference_images: [
      { tipo: 'producto', product_id: p.varianteId },
      ...(p.uso === 'integrado' && p.logoUrl ? [{ tipo: 'logo' as const, url: p.logoUrl }] : []),
    ],
  }));
}
